import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Package, DollarSign, ArrowRight } from "lucide-react";
import { brandService } from "../../lib/brandService";
import { toast } from "sonner";
import Header from "../Header";

const BrandOptionsPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBrand();
  }, [brandId]);

  const loadBrand = async () => {
    setLoading(true);
    try {
      const result = await brandService.getAllBrands();
      if (result.success) {
        const foundBrand = result.data.find(
          (b) =>
            String(b.id) === String(brandId) ||
            b.brand_key === brandId ||
            b.brandKey === brandId
        );
        if (foundBrand) {
          setBrand(foundBrand);
        } else {
          toast.error("Brand not found");
          navigate("/brands");
        }
      } else {
        toast.error(result.error);
        navigate("/brands");
      }
    } catch (error) {
      toast.error("Failed to load brand");
      console.error("Error loading brand:", error);
      navigate("/brands");
    } finally {
      setLoading(false);
    }
  };

  const handleProductsClick = () => {
    navigate(`/products?brand=${encodeURIComponent(brandId)}`);
  };

  const handleEditCommissionsClick = () => {
    navigate(`/brands/${brandId}/edit-commissions`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B8460E]"></div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <div className="text-center space-y-3">
          <p className="text-lg font-semibold">Brand not loaded</p>
          <p className="text-sm text-gray-400">
            We couldn&apos;t load this brand. Please return to the brand list.
          </p>
          <button
            onClick={() => navigate("/brands")}
            className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20 transition"
          >
            Back to Brands
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black bg-gradient-to-br from-black via-gray-900 to-black">
      <Header />

      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-[#B8460E] rounded-full blur-3xl opacity-20 animate-pulse"></div>
        <div
          className="absolute bottom-20 right-20 w-96 h-96 bg-[#8B3A0E] rounded-full blur-3xl opacity-15 animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto relative z-10">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">{brand.name}</h1>
            {brand.description && (
              <p className="text-gray-400">{brand.description}</p>
            )}
          </div>

          {/* Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Products Option */}
            <button
              onClick={handleProductsClick}
              className="group bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-left"
            >
              {/* Animated Background Elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
                <div className="absolute w-32 h-32 bg-[#B8460E]/5 rounded-full blur-2xl animate-pulse -top-8 -right-8"></div>
                <div className="absolute w-24 h-24 bg-[#8B3A0E]/5 rounded-full blur-xl animate-pulse -bottom-4 -left-4"></div>
              </div>

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-16 h-16 bg-[#B8460E]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#B8460E]/30 transition-all duration-300">
                  <Package className="w-8 h-8 text-[#B8460E]" />
                </div>

                {/* Title & Description */}
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#B8460E] transition-colors duration-200">
                  Products
                </h3>
                <p className="text-gray-400 mb-4">
                  View and manage products for this brand
                </p>

                {/* Arrow */}
                <div className="flex items-center gap-2 text-[#B8460E]">
                  <span className="text-sm font-medium">View Products</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </div>
            </button>

            {/* Edit Commissions Option */}
            <button
              onClick={handleEditCommissionsClick}
              className="group bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-left"
            >
              {/* Animated Background Elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
                <div className="absolute w-32 h-32 bg-[#B8460E]/5 rounded-full blur-2xl animate-pulse -top-8 -right-8"></div>
                <div className="absolute w-24 h-24 bg-[#8B3A0E]/5 rounded-full blur-xl animate-pulse -bottom-4 -left-4"></div>
              </div>

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-16 h-16 bg-[#B8460E]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#B8460E]/30 transition-all duration-300">
                  <DollarSign className="w-8 h-8 text-[#B8460E]" />
                </div>

                {/* Title & Description */}
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#B8460E] transition-colors duration-200">
                  Edit Commissions
                </h3>
                <p className="text-gray-400 mb-4">
                  Configure commission tiers for this brand
                </p>

                {/* Arrow */}
                <div className="flex items-center gap-2 text-[#B8460E]">
                  <span className="text-sm font-medium">Edit Tiers</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandOptionsPage;
